import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import 'dayjs/locale/ar'
import relativeTime from 'dayjs/plugin/relativeTime'
import { DATE_FORMAT, DATETIME_FORMAT, TIME_FORMAT, CURRENCY_SYMBOLS, DEFAULT_CURRENCY } from './constants'

dayjs.extend(relativeTime)

/**
 * Formate une date calendrier pour l'API (LocalDate Java).
 * Évite le décalage d'un jour : un Date issu de minuit local devient une ISO en UTC
 * (ex. veille 23h UTC) et le backend peut enregistrer le mauvais jour civil.
 *
 * @param {string|Date|number|null|undefined} value
 * @returns {string|null} YYYY-MM-DD ou null si vide / invalide
 */
export const formatLocalDateForApi = (value) => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear()
    const m = String(value.getMonth() + 1).padStart(2, '0')
    const d = String(value.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  const parsed = dayjs(value)
  if (!parsed.isValid()) return null
  return parsed.format('YYYY-MM-DD')
}

/**
 * Début de journée pour filtre API (LocalDateTime Java).
 * @param {string|Date|null|undefined} value
 * @returns {string|null} ex. 2026-06-25T00:00:00
 */
export const toApiDateTimeStartOfDay = (value) => {
  const date = formatLocalDateForApi(value)
  return date ? `${date}T00:00:00` : null
}

/**
 * Fin de journée pour filtre API (LocalDateTime Java).
 * @param {string|Date|null|undefined} value
 * @returns {string|null} ex. 2026-06-25T23:59:59
 */
export const toApiDateTimeEndOfDay = (value) => {
  const date = formatLocalDateForApi(value)
  return date ? `${date}T23:59:59` : null
}

/**
 * Normalise les paramètres de liste des sessions caisse (dates → LocalDateTime).
 */
export const buildCaisseSessionListParams = ({
  pointId,
  status,
  from,
  to,
  dateDebut,
  dateFin,
  limit,
} = {}) => {
  const params = {}
  if (pointId) params.pointId = pointId
  if (status) params.status = status
  const fromValue = from ?? dateDebut
  const toValue = to ?? dateFin
  const fromDt = toApiDateTimeStartOfDay(fromValue)
  const toDt = toApiDateTimeEndOfDay(toValue)
  if (fromDt) params.from = fromDt
  if (toDt) params.to = toDt
  if (limit != null) params.limit = limit
  return params
}

/**
 * Format currency value
 * @param {number} value - The numeric value
 * @param {string} currency - Currency code (EUR, USD, TND)
 * @param {string} locale - Locale code
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = DEFAULT_CURRENCY, locale = 'fr-FR') => {
  if (value === null || value === undefined || isNaN(value)) return '-'
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(value)
  } catch (error) {
    // Fallback if Intl is not supported
    const symbol = CURRENCY_SYMBOLS[currency] || currency
    return `${value.toFixed(3)} ${symbol}`
  }
}

/** Montant sans symbole devise (tableaux denses) */
export const formatCurrencyCompact = (value, locale = 'fr-FR') => {
  if (value === null || value === undefined || isNaN(value)) return '—'
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(value)
}

/**
 * Format number with thousands separator
 * @param {number} value - The numeric value
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) return '-'
  
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * Format quantity with up to 3 decimals (comma as decimal separator).
 * @param {number|string} value
 * @returns {string}
 */
export const formatQuantity = (value) => {
  if (value === null || value === undefined || value === '') return '-'
  const parsed = Number.parseFloat(String(value).replace(/\s/g, '').replace(',', '.'))
  if (!Number.isFinite(parsed)) return '-'
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(parsed)
}

/**
 * Format percentage
 * @param {number} value - The numeric value (0-100)
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '-'
  return `${value.toFixed(decimals)}%`
}

/**
 * Format date
 * @param {string|Date} date - Date to format
 * @param {string} format - Format string
 * @param {string} locale - Locale code
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = DATE_FORMAT, locale = 'fr') => {
  if (!date) return '-'
  return dayjs(date).locale(locale).format(format)
}

/**
 * Format datetime
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale code
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (date, locale = 'fr') => {
  return formatDate(date, DATETIME_FORMAT, locale)
}

/**
 * Format time
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted time string
 */
export const formatTime = date => {
  return formatDate(date, TIME_FORMAT)
}

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale code
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date, locale = 'fr') => {
  if (!date) return '-'
  return dayjs(date).locale(locale).fromNow()
}

/**
 * Format file size
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size string
 */
export const formatFileSize = bytes => {
  if (bytes === 0) return '0 B'
  if (!bytes) return '-'
  
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return `${text.substring(0, maxLength)}...`
}

/**
 * Format phone number
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone number
 */
export const formatPhone = phone => {
  if (!phone) return '-'
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '')
  
  // Format French phone numbers (10 digits)
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')
  }
  
  // Format international numbers
  if (cleaned.length > 10) {
    return `+${cleaned}`
  }
  
  return phone
}

/**
 * Format SIRET number
 * @param {string} siret - SIRET number
 * @returns {string} Formatted SIRET
 */
export const formatSiret = siret => {
  if (!siret) return '-'
  const cleaned = siret.replace(/\s/g, '')
  if (cleaned.length === 14) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, '$1 $2 $3 $4')
  }
  return siret
}

/**
 * Capitalize first letter
 * @param {string} text - Text to capitalize
 * @returns {string} Capitalized text
 */
export const capitalize = text => {
  if (!text) return ''
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

/**
 * Format status label
 * @param {string} status - Status code
 * @returns {string} Formatted status
 */
export const formatStatus = status => {
  if (!status) return '-'
  return status.replace(/_/g, ' ').toLowerCase().split(' ').map(capitalize).join(' ')
}

/**
 * Get initials from name
 * @param {string} name - Full name
 * @returns {string} Initials
 */
export const getInitials = name => {
  if (!name) return '?'
  
  const parts = name.trim().split(' ')
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase()
  }
  
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

/**
 * Format address
 * @param {object} address - Address object
 * @returns {string} Formatted address
 */
export const formatAddress = address => {
  if (!address) return '-'
  
  const parts = [
    address.street,
    address.city,
    address.postalCode,
    address.country,
  ].filter(Boolean)
  
  return parts.join(', ')
}

/**
 * Parse number from formatted string
 * @param {string} value - Formatted number string
 * @returns {number} Parsed number
 */
export const parseNumber = value => {
  if (typeof value === 'number') return value
  if (!value) return 0
  
  // Remove spaces and replace comma with dot
  const cleaned = value.toString().replace(/\s/g, '').replace(',', '.')
  const parsed = parseFloat(cleaned)
  
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Format TVA rate
 * @param {number} rate - TVA rate (0-100)
 * @returns {string} Formatted TVA rate
 */
export const formatTVA = rate => {
  return `${rate}%`
}

/**
 * Calculate TVA amount
 * @param {number} amount - Base amount
 * @param {number} rate - TVA rate (0-100)
 * @returns {number} TVA amount
 */
export const calculateTVA = (amount, rate) => {
  return (amount * rate) / 100
}

/**
 * Calculate total with TVA
 * @param {number} amount - Base amount
 * @param {number} rate - TVA rate (0-100)
 * @returns {number} Total with TVA
 */
export const calculateTotalWithTVA = (amount, rate) => {
  return amount + calculateTVA(amount, rate)
}


